import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MapPin, Bed, Bath, Square, Eye, Heart, Star, MoveRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface PropertyCardProps {
  property: {
    id: string;
    title: string;
    address: string;
    monthly_price: number;
    type: string;
    bedrooms: number;
    bathrooms: number;
    area: number;
    occupied: boolean;
    views: number;
    furnishing_status: string;
  };
  onSave?: (propertyId: string) => void;
}

const PropertyCard = ({ property, onSave }: PropertyCardProps) => {
  const [mainImage, setMainImage] = useState<string | null>(null);
  const [isSaved, setIsSaved] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [isHovering, setIsHovering] = useState(false);

  useEffect(() => {
    fetchMainImage();
    checkIfSaved();
  }, [property.id]);

  const fetchMainImage = async () => {
    try {
      setImageLoading(true);
      const { data: images, error } = await supabase
        .from('property_images')
        .select('*')
        .eq('property_id', property.id)
        .order('image_order', { ascending: true });

      if (error) {
        setMainImage(null);
        return;
      }

      if (images && images.length > 0) {
        let mainImageData = images.find(img => img.storage_path && img.storage_path.trim() !== '');
        if (!mainImageData) mainImageData = images.find(img => img.is_main);
        if (!mainImageData) mainImageData = images[0];

        let imageUrl: string | null = null;

        if (mainImageData.storage_path && mainImageData.storage_path.trim() !== '') {
          const { data } = supabase.storage
            .from('property-images')
            .getPublicUrl(mainImageData.storage_path);
          imageUrl = data.publicUrl;
        } else if (mainImageData.url && !mainImageData.url.startsWith('blob:')) {
          imageUrl = mainImageData.url;
        }

        setMainImage(imageUrl);
      } else {
        setMainImage(null);
      }
    } catch (error) {
      setMainImage(null);
    } finally {
      setImageLoading(false);
    }
  };

  const checkIfSaved = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from('saved_properties')
        .select('id')
        .eq('user_id', user.id)
        .eq('property_id', property.id)
        .maybeSingle();

      setIsSaved(!!data);
    } catch (error) {
      setIsSaved(false);
    }
  };

  const handleSaveProperty = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        alert('يجب تسجيل الدخول لحفظ العقار');
        return;
      }

      if (isSaved) {
        await supabase
          .from('saved_properties')
          .delete()
          .eq('user_id', user.id)
          .eq('property_id', property.id);
        setIsSaved(false);
      } else {
        await supabase
          .from('saved_properties')
          .insert({
            user_id: user.id,
            property_id: property.id
          });
        setIsSaved(true);
      }

      if (onSave) {
        onSave(property.id);
      }
    } catch (error) {
      console.error('Error saving property:', error);
    }
  };

  return (
    <Card 
      className="overflow-hidden transition-all duration-300 hover:shadow-2xl border border-gray-200 rounded-2xl bg-gradient-to-br from-white via-white to-gray-50"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div className="relative">
        {/* صورة العقار الرئيسية مع تأثيرات */}
        <div className="h-64 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center overflow-hidden relative rounded-t-2xl">
          {imageLoading ? (
            <div className="animate-pulse w-full h-full flex items-center justify-center">
              <div className="bg-gradient-to-br from-gray-200 to-gray-300 border-2 border-dashed rounded-xl w-full h-full" />
            </div>
          ) : mainImage ? (
            <img
              src={mainImage}
              alt={property.title}
              className={`w-full h-full object-cover transition-all duration-700 ${isHovering ? 'scale-110' : 'scale-100'}`}
              onError={() => setMainImage(null)}
            />
          ) : (
            <div className="flex flex-col items-center justify-center text-gray-400 p-4">
              <div className="bg-gray-200 border-2 border-dashed rounded-xl w-16 h-16 flex items-center justify-center mb-2">
                <Square className="h-8 w-8" />
              </div>
              <p className="text-center">صورة العقار غير متوفرة</p>
            </div>
          )}
          
          {/* طبقة تدرج فوق الصورة */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
          
          {/* السعر في الزاوية السفلية اليسرى */}
          <div className="absolute bottom-4 left-4 text-white z-10">
            <div className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-700 px-3 py-1 rounded-lg shadow-lg">
              {property.monthly_price.toLocaleString('ar')} ر.ع
            </div>
            <div className="text-sm font-light mt-1 text-gray-200">شهرياً</div>
          </div>
          
          {/* شارة الحالة في الزاوية العلوية اليسرى */}
          <div className="absolute top-4 left-4 z-10">
            <Badge 
              variant={property.occupied ? "secondary" : "default"} 
              className={`font-medium py-1.5 px-3 rounded-full ${property.occupied ? 'bg-rose-100 text-rose-800' : 'bg-emerald-100 text-emerald-800'}`}
            >
              {property.occupied ? "مؤجر" : "متاح"}
            </Badge>
          </div>
          
          {/* أزرار الإجراءات في الزاوية العلوية اليمنى */}
          <div className="absolute top-4 right-4 z-10 flex gap-2">
            <Button
              variant="ghost"
              size="icon"
              className={`rounded-full bg-white/90 backdrop-blur-sm hover:bg-white transition-all ${
                isSaved 
                  ? 'text-red-500 hover:text-red-600 shadow-md' 
                  : 'text-gray-600 hover:text-gray-800'
              } ${isHovering ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity duration-300`}
              onClick={handleSaveProperty}
            >
              <Heart className={`h-5 w-5 ${isSaved ? 'fill-current' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      <CardContent className="p-5">
        {/* العنوان والعنوان الفرعي */}
        <div className="mb-4">
          <h3 className="font-bold text-xl text-gray-900 mb-1 line-clamp-1 text-right hover:text-blue-600 transition-colors">
            {property.title}
          </h3>
          <div className="flex items-start gap-1 text-gray-600 text-sm">
            <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0 text-blue-500" />
            <span className="text-right line-clamp-2">{property.address}</span>
          </div>
        </div>

        {/* النوع وحالة الأثاث */}
        <div className="flex flex-wrap gap-2 mb-4">
          <Badge className="py-1 px-3 rounded-full bg-blue-50 text-blue-800 border border-blue-100">
            {property.type}
          </Badge>
          <Badge className="py-1 px-3 rounded-full bg-amber-50 text-amber-800 border border-amber-100">
            {property.furnishing_status === 'furnished' ? 'مفروش' : 
             property.furnishing_status === 'semi_furnished' ? 'مفروش جزئياً' : 'غير مفروش'}
          </Badge>
        </div>

        {/* المواصفات الأساسية */}
        <div className="grid grid-cols-4 gap-3 mb-5">
          <div className="flex flex-col items-center text-center">
            <div className="bg-blue-50 p-2 rounded-lg mb-1 border border-blue-100">
              <Bed className="h-5 w-5 text-blue-600" />
            </div>
            <span className="text-sm font-medium text-gray-800">{property.bedrooms}</span>
            <span className="text-xs text-gray-500">غرف</span>
          </div>
          
          <div className="flex flex-col items-center text-center">
            <div className="bg-teal-50 p-2 rounded-lg mb-1 border border-teal-100">
              <Bath className="h-5 w-5 text-teal-600" />
            </div>
            <span className="text-sm font-medium text-gray-800">{property.bathrooms}</span>
            <span className="text-xs text-gray-500">حمام</span>
          </div>
          
          <div className="flex flex-col items-center text-center">
            <div className="bg-amber-50 p-2 rounded-lg mb-1 border border-amber-100">
              <Square className="h-5 w-5 text-amber-600" />
            </div>
            <span className="text-sm font-medium text-gray-800">{property.area}</span>
            <span className="text-xs text-gray-500">م²</span>
          </div>
          
          <div className="flex flex-col items-center text-center">
            <div className="bg-purple-50 p-2 rounded-lg mb-1 border border-purple-100">
              <Eye className="h-5 w-5 text-purple-600" />
            </div>
            <span className="text-sm font-medium text-gray-800">{property.views || 0}</span>
            <span className="text-xs text-gray-500">مشاهدات</span>
          </div>
        </div>

        {/* زر عرض التفاصيل */}
        <Link to={`/property/${property.id}`}>
          <Button className="w-full rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5 group">
            <span className="flex items-center justify-center w-full">
              <span>عرض التفاصيل</span>
              <MoveRight className="h-4 w-4 mr-2 transform group-hover:translate-x-1 transition-transform" />
            </span>
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
};

export default PropertyCard;